export type CustomerActivityType = 'group_class' | 'event' | 'private_booking' | 'course'

export interface CustomerActivitySource {
    courseId?: string | null
    activityType?: unknown
    bookingType?: unknown
    eventType?: unknown
    eventKind?: unknown
    isCyclical?: unknown
}

const ACTIVITY_TYPES = new Set<CustomerActivityType>([
    'course',
    'group_class',
    'event',
    'private_booking',
])

const normalizeType = (value: unknown): CustomerActivityType | null => {
    if (typeof value !== 'string') return null
    const normalized = value.trim().toLowerCase() as CustomerActivityType
    return ACTIVITY_TYPES.has(normalized) ? normalized : null
}

/**
 * Normalizes the activity discriminator at the API/view-model boundary.
 * Presentation components must use the resolved route instead of branching between families.
 */
export const resolveCustomerActivityType = (
    source?: CustomerActivitySource | null
): CustomerActivityType | null => {
    if (!source) return null
    if (source.courseId) return 'course'

    const explicit = normalizeType(source.activityType) ?? normalizeType(source.bookingType)
    if (explicit) return explicit

    const eventKind = typeof source.eventKind === 'string'
        ? source.eventKind.trim().toLowerCase()
        : null
    const eventType = typeof source.eventType === 'string'
        ? source.eventType.trim().toLowerCase()
        : null
    if (eventKind === 'private_booking' || eventType === 'private_booking') {
        return 'private_booking'
    }
    if (source.isCyclical === false) return 'event'
    if (source.isCyclical === true) return 'group_class'
    return null
}

const encodedId = (eventId: string) => encodeURIComponent(eventId)

export const customerActivityDetailsHref = (
    type: CustomerActivityType,
    eventId: string
): string => {
    const id = encodedId(eventId)
    if (type === 'course') return `/courses/${id}`
    if (type === 'event') return `/calendar/events/${id}`
    if (type === 'private_booking') return `/calendar/individual/${id}`
    return `/calendar/classes/${id}`
}

export const customerActivityBookHref = (
    type: CustomerActivityType,
    eventId: string
): string | null => {
    if (type === 'private_booking' || type === 'course') return null
    return `${customerActivityDetailsHref(type, eventId)}/book`
}

export const customerActivityCancelHref = (
    type: CustomerActivityType,
    eventId: string,
    signupId?: string | null
): string => {
    if (type === 'course') return customerActivityDetailsHref(type, eventId)
    const href = `${customerActivityDetailsHref(type, eventId)}/cancel`
    return signupId ? `${href}?signupId=${encodeURIComponent(signupId)}` : href
}

export const customerActivityFallbackHref = (
    type: CustomerActivityType
): '/calendar' | '/calendar/events' | '/calendar/individual' => {
    if (type === 'event') return '/calendar/events'
    if (type === 'private_booking') return '/calendar/individual'
    return '/calendar'
}

export const unknownCustomerActivityHref = (eventId: string): string =>
    `/calendar/activity/${encodedId(eventId)}`
